/**
 * React Query bindings for the knowledge-base module
 * (`services/knowledge-base.service.ts`). The hooks move data and refresh what
 * a change touches; what a screen says about the outcome — a toast, a line in
 * a dialog — is the screen's.
 */

import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type KnowledgeBase,
  type KnowledgeBaseFile,
  createKnowledgeBase,
  deleteFile,
  deleteFiles,
  deleteKnowledgeBase,
  getDownloadLink,
  getUsage,
  keepDescription,
  listFiles,
  listKnowledgeBases,
  listPassages,
  retryFile,
  retryFiles,
  testSearch,
  updateKnowledgeBaseDescription,
  writeDescription,
} from "@/services/knowledge-base.service";

/** How often what is still changing is read again. */
export const POLL_MS = 3000;

const keys = {
  list: (workspaceId: string) => ["kb-stores", workspaceId],
  usage: (workspaceId: string) => ["kb-usage", workspaceId],
  files: (storeId: string) => ["kb-files", storeId],
  passages: (fileId: string, page: number) => ["kb-passages", fileId, page],
};

/** Read again every 3 s until `until` (a time in ms). */
const pollingUntil = (until: number) => () => (Date.now() < until ? POLL_MS : false);

/** A workspace's knowledge bases: the page's list and the agent editor's picker. */
export const useKbStores = (workspaceId?: string, pollUntil = 0) =>
  useQuery({
    queryKey: keys.list(workspaceId),
    queryFn: () => listKnowledgeBases(workspaceId),
    enabled: !!workspaceId,
    refetchInterval: pollingUntil(pollUntil),
  });

/** The plan's limits and what the workspace uses of them. */
export const useKbUsage = (workspaceId: string, pollUntil = 0) =>
  useQuery({
    queryKey: keys.usage(workspaceId),
    queryFn: () => getUsage(workspaceId),
    refetchInterval: pollingUntil(pollUntil),
  });

/** A file still on its way: being uploaded, or processed by the worker. */
export const isUnfinished = (file: Pick<KnowledgeBaseFile, "status">) =>
  file.status === "uploading" || file.status === "processing";

/** A knowledge base's files, read again every 3 s while one is unfinished. */
export const useKbFiles = (storeId?: string) =>
  useQuery({
    queryKey: keys.files(storeId),
    queryFn: () => listFiles(storeId),
    enabled: !!storeId,
    refetchInterval: (query) => (query.state.data?.some(isUnfinished) ? POLL_MS : false),
  });

/** A page (from 1) of a ready file's passages: its preview. A ready file's passages never change (a
 * replacement is another file), so a page is read once. */
export const useKbPassages = (fileId: string, page: number) =>
  useQuery({ queryKey: keys.passages(fileId, page), queryFn: () => listPassages(fileId, page), staleTime: Infinity });

const refreshWorkspace = (queryClient: QueryClient, workspaceId: string) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: keys.list(workspaceId) }),
    queryClient.invalidateQueries({ queryKey: keys.usage(workspaceId) }),
  ]);

/** What a change to a file touches: its knowledge base's files, the counts in the list, the plan's usage. */
export function useRefreshFiles(storeId: string, workspaceId: string) {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: keys.files(storeId) }),
      refreshWorkspace(queryClient, workspaceId),
    ]);
}

export const useCreateKnowledgeBase = (workspaceId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (displayName: string) => createKnowledgeBase(workspaceId, displayName),
    onSuccess: () => refreshWorkspace(queryClient, workspaceId),
  });
};

export const useDeleteKnowledgeBase = (workspaceId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteKnowledgeBase(id),
    onSuccess: () => refreshWorkspace(queryClient, workspaceId),
  });
};

/** A description change answers with the knowledge base as it now is, which the list shows at once. */
function useDescriptionChange<V>(workspaceId: string, change: (variables: V) => Promise<KnowledgeBase>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: change,
    onSuccess: (changed) =>
      queryClient.setQueryData<KnowledgeBase[]>(keys.list(workspaceId), (list) =>
        list?.map((kb) => (kb.id === changed.id ? changed : kb)),
      ),
  });
}

/** A hand edit. */
export const useUpdateKbDescription = (workspaceId: string) =>
  useDescriptionChange(workspaceId, ({ storeId, description }: { storeId: string; description: string | null }) =>
    updateKnowledgeBaseDescription(storeId, description),
  );

/** "Keep mine". */
export const useKeepDescription = (workspaceId: string) => useDescriptionChange(workspaceId, keepDescription);

/** "Regenerate", and "Update" on the review question. */
export const useWriteDescription = (workspaceId: string) => useDescriptionChange(workspaceId, writeDescription);

export const useRetryFile = (storeId: string, workspaceId: string) =>
  useMutation({ mutationFn: retryFile, onSuccess: useRefreshFiles(storeId, workspaceId) });

/** Delete, or Cancel an unfinished upload. */
export const useDeleteFile = (storeId: string, workspaceId: string) =>
  useMutation({ mutationFn: deleteFile, onSuccess: useRefreshFiles(storeId, workspaceId) });

/** Retry of the ticked files. */
export const useRetryFiles = (storeId: string, workspaceId: string) =>
  useMutation({
    mutationFn: (fileIds: string[]) => retryFiles(storeId, fileIds),
    onSuccess: useRefreshFiles(storeId, workspaceId),
  });

/** Delete of the ticked files. */
export const useDeleteFiles = (storeId: string, workspaceId: string) =>
  useMutation({
    mutationFn: (fileIds: string[]) => deleteFiles(storeId, fileIds),
    onSuccess: useRefreshFiles(storeId, workspaceId),
  });

/** A file's download link; nothing changes. */
export const useDownloadLink = () => useMutation({ mutationFn: getDownloadLink });

/** The test console's search; nothing changes. */
export const useTestSearch = () => useMutation({ mutationFn: testSearch });
